import React, { useState } from 'react';
import { fmtDate } from '../finance/shared';
import { whatsappLink, copyToClipboard } from './phoneUtils';
import { subscriptionBadge } from './subscriptionUtils';
import ActionsMenu from './ActionsMenu';
import Icon from '../../../components/dashboard/DashboardIcons';
import { WhatsAppMessageModal } from '../../../components/whatsapp/WhatsAppMessageModal';

const waVariablesFor = (p) => {
  const remaining = (p.subscription_amount ?? null) !== null && (p.subscription_paid_amount ?? null) !== null
    ? Math.max(p.subscription_amount - p.subscription_paid_amount, 0) : null;
  return {
    guardian_name: p.guardian_name || 'ولي الأمر',
    player_name: p.name,
    subscription_name: p.subscription_package || '',
    expiry_date: fmtDate(p.subscription_end),
    paid_amount: p.subscription_paid_amount ?? '',
    remaining_amount: remaining ?? '',
    payment_reference: p.subscription_payment_reference || '',
  };
};

const POSITIONS = { goalkeeper: 'حارس مرمى', defender: 'مدافع', midfielder: 'وسط', forward: 'مهاجم' };

const Avatar = ({ p, size = 'h-9 w-9' }) => p.photo_url ? (
  <img src={p.photo_url} alt="" className={`${size} rounded-full bg-slate-100 object-cover`} onError={(e) => { e.target.style.display = 'none'; }} />
) : (
  <div className={`${size} flex items-center justify-center rounded-full bg-[#16233b]/10 text-xs font-bold text-[#16233b]`}>{(p.name || '؟').slice(0, 2)}</div>
);

const PhoneCell = ({ phone, notify }) => {
  if (!phone) return <span className="text-xs text-slate-300">—</span>;
  const wa = whatsappLink(phone);
  return (
    <div className="flex items-center gap-1 text-xs text-slate-500" dir="ltr">
      <span>{phone}</span>
      {wa && <a href={wa} target="_blank" rel="noreferrer" title="واتساب" className="rounded p-1 text-teal-600 hover:bg-teal-50" data-testid="player-whatsapp-link"><Icon name="whatsapp" className="w-3.5 h-3.5" /></a>}
      <button title="نسخ الرقم" onClick={() => { copyToClipboard(phone); notify?.('تم نسخ الرقم'); }} className="rounded p-1 text-slate-400 hover:bg-slate-100" data-testid="player-copy-phone"><Icon name="copy" className="w-3.5 h-3.5" /></button>
    </div>
  );
};

export default function PlayersTable({ players, categories, catName, onView, onEdit, onCard, onToggle, onArchive, onDelete, canDelete, canWhatsApp, notify, selected, onToggleSelect, onToggleSelectAll }) {
  const [waPlayer, setWaPlayer] = useState(null);
  const outOfRange = (p) => {
    if (p.age == null) return false;
    const c = categories?.find((c2) => c2.id === p.category_id);
    if (!c) return false;
    if (c.min_age != null && p.age < c.min_age) return true;
    if (c.max_age != null && p.age > c.max_age) return true;
    return false;
  };
  const rowActions = (p) => [
    { icon: 'users', label: 'عرض ملف اللاعب', onClick: () => onView(p), testId: 'player-action-view' },
    { icon: 'cog', label: 'تعديل بيانات اللاعب', onClick: () => onEdit(p), testId: 'player-action-edit' },
    canWhatsApp && { icon: 'whatsapp', label: 'مراسلة عبر واتساب', onClick: () => setWaPlayer(p), testId: 'player-action-whatsapp' },
    { icon: 'document', label: 'طباعة البطاقة و QR', onClick: () => onCard(p), testId: 'player-action-card' },
    { icon: p.is_active ? 'x' : 'check', label: p.is_active ? 'تعطيل اللاعب' : 'تفعيل اللاعب', onClick: () => onToggle(p), testId: 'player-action-toggle' },
    onArchive && { icon: 'archive', label: 'أرشفة اللاعب', onClick: () => onArchive(p), testId: 'player-action-archive' },
    canDelete && { icon: 'trash', label: 'حذف نهائي', onClick: () => onDelete(p), testId: 'player-action-delete', danger: true },
  ];
  const allSelected = players.length > 0 && players.every((p) => selected?.has(p.id));

  return (
    <>
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
            <th className="px-3 py-2"><input type="checkbox" checked={allSelected} onChange={() => onToggleSelectAll(players.map((p) => p.id))} data-testid="players-select-all" /></th>
            <th className="px-4 py-2 font-semibold">اللاعب</th>
            <th className="px-2 py-2 font-semibold">الرقم</th>
            <th className="px-2 py-2 font-semibold">الفئة</th>
            <th className="px-2 py-2 font-semibold">المركز</th>
            <th className="px-2 py-2 font-semibold">العمر</th>
            <th className="px-2 py-2 font-semibold">حالة اللاعب</th>
            <th className="px-2 py-2 font-semibold">ملخص الاشتراك</th>
            <th className="px-2 py-2 font-semibold"></th>
          </tr></thead>
          <tbody>
            {players.map((p) => {
              const badge = subscriptionBadge(p);
              return (
                <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="player-row">
                  <td className="px-3 py-2"><input type="checkbox" checked={Boolean(selected?.has(p.id))} onChange={() => onToggleSelect(p.id)} data-testid="player-select-checkbox" /></td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Avatar p={p} />
                      <div className="min-w-0">
                        <button onClick={() => onView(p)} className="font-semibold text-slate-800 hover:text-teal-700" data-testid="player-view-btn">{p.name}</button>
                        <PhoneCell phone={p.phone || p.guardian_phone} notify={notify} />
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2 text-slate-600">{p.jersey_number ?? '—'}</td>
                  <td className="px-2 py-2 text-slate-600">{catName(p.category_id)}</td>
                  <td className="px-2 py-2 text-slate-600">{POSITIONS[p.position] || '—'}</td>
                  <td className="px-2 py-2 text-slate-600">
                    <span className="inline-flex items-center gap-1" data-testid="player-age-cell">
                      {p.age ?? '—'}
                      {outOfRange(p) && <Icon name="warning" className="w-3.5 h-3.5 text-amber-500" title="العمر خارج نطاق الفئة" data-testid="player-age-out-of-range" />}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${p.is_active ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>{p.is_active ? 'نشط' : 'معطل'}</span>
                  </td>
                  <td className="px-2 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${badge.cls}`} data-testid="player-sub-badge">{badge.label}</span></td>
                  <td className="px-2 py-2"><div className="flex justify-end"><ActionsMenu items={rowActions(p)} testId="player-actions-btn" /></div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="space-y-2 p-3 lg:hidden">
        {players.map((p) => {
          const badge = subscriptionBadge(p);
          return (
            <div key={p.id} className="rounded-xl border border-slate-200 p-3" data-testid="player-card-mobile">
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <input type="checkbox" checked={Boolean(selected?.has(p.id))} onChange={() => onToggleSelect(p.id)} data-testid="player-select-checkbox-mobile" />
                  <Avatar p={p} />
                  <div className="min-w-0">
                    <button onClick={() => onView(p)} className="block truncate font-semibold text-slate-800 hover:text-teal-700">{p.name}</button>
                    <PhoneCell phone={p.phone || p.guardian_phone} notify={notify} />
                  </div>
                </div>
                <ActionsMenu items={rowActions(p)} testId="player-actions-btn-mobile" />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                <span>#{p.jersey_number ?? '—'}</span>
                <span>· {catName(p.category_id)}</span>
                <span>· {POSITIONS[p.position] || 'بلا مركز'}</span>
                <span className={`mr-auto rounded-full px-2 py-0.5 text-[10px] font-bold ${p.is_active ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>{p.is_active ? 'نشط' : 'معطل'}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${badge.cls}`}>{badge.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {waPlayer && (
        <WhatsAppMessageModal
          open={Boolean(waPlayer)}
          onClose={() => setWaPlayer(null)}
          recipient={{ id: waPlayer.id, name: waPlayer.guardian_name || waPlayer.name, phone: waPlayer.guardian_phone || waPlayer.phone, type: 'player_guardian' }}
          variables={waVariablesFor(waPlayer)}
          relatedType="player"
          relatedId={waPlayer.id}
        />
      )}
    </>
  );
}
